import { Plugin, PluginKey, EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Node } from 'prosemirror-model';

export const multiplierPluginKey = new PluginKey<number>('multiplier');

// WeakMap cache for multiplier lookups
const multiplierCache = new WeakMap<Node, number>();

/**
 * Find the multiplier value from the document by searching for text with the multiplier mark.
 * Uses WeakMap memoization to avoid repeated traversals.
 */
export function findMultiplier(doc: Node): number {
  const cached = multiplierCache.get(doc);
  if (cached !== undefined) return cached;

  let multiplier = 1;
  doc.descendants((node) => {
    if (node.isText && node.marks.length > 0) {
      for (const mark of node.marks) {
        if (mark.type.name === 'multiplier') {
          const parsed = parseFloat(node.text || '1');
          multiplier = isNaN(parsed) ? 1 : parsed;
          return false;
        }
      }
    }
    return true;
  });

  multiplierCache.set(doc, multiplier);
  return multiplier;
}

/**
 * Plugin that caches the multiplier value in plugin state.
 * Recalculates only when the document changes.
 */
export function multiplierPlugin(): Plugin<number> {
  return new Plugin({
    key: multiplierPluginKey,
    state: {
      init(_, state) {
        return findMultiplier(state.doc);
      },
      apply(tr, value, _oldState, newState) {
        // Only recalculate when document changes
        if (!tr.docChanged) return value;
        return findMultiplier(newState.doc);
      },
    },
  });
}

/**
 * Get the cached multiplier value from plugin state.
 * Falls back to 1 if plugin state is not available.
 */
export function getMultiplier(state: EditorState): number {
  const value = multiplierPluginKey.getState(state);
  return value ?? 1;
}

/**
 * Find the multiplier value from an EditorView.
 * Uses plugin state when available (preferred), falls back to doc traversal.
 */
export function findMultiplierFromDoc(view: EditorView): number {
  const pluginValue = multiplierPluginKey.getState(view.state);
  if (pluginValue !== undefined) return pluginValue;
  return findMultiplier(view.state.doc);
}
